<template>
	<div class="bg-white px-6 lg:px-12 py-8 lg:py-10">
		<h3 class="mb-2">Tracking Information</h3>
		<div class="grid grid-cols-1 md:grid-cols-2 gap-10 mt-10">
			<div>
				<p class="lg:text-sm 2xl:text-base mb-2">BOL Number</p>
				<h5 class="lg:text-6xl">{{ getBolNumber }}</h5>
			</div>
			<div>
				<p class="lg:text-sm 2xl:text-base mb-2">Booking Number</p>
				<h5 class="lg:text-6xl">{{ getBookingNumber }}</h5>
			</div>
		</div>
		<div class="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-y-6 mt-10">
			<div v-for="(result, index) in getPorts" :key="index">
				<p class="lg:text-sm 2xl:text-base mb-2">{{ index | portLabel(getPorts.length - 1) }}</p>
				<h5 class="lg:text-6xl">{{ result.port }}</h5>
				<div class="mt-2">
					<span
						v-html="
							arrivalText(
								result.expArrivalDate,
								result.arrivalDate,
								index === 0 || index === getPorts.length - 1 ? 'text-red' : 'text-gray-dark',
							)
						"
					></span>
					<span
						v-html="
							departureText(
								result.expDepartureDate,
								result.departureDate,
								index === 0 || index === getPorts.length - 1 ? 'text-red' : 'text-gray-dark',
							)
						"
					></span>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
/**
    title: Tracking Results
	props: {
		- results: Get the results which were passed to this component
	}
    computed: {
        - getBolNumber: Get the BOL number from the results
        - getBookingNumber: Get the booking number from the results
		- getPorts: Get the ports from the results.
    }
	filters: {
		- formatDate: Format the date from the results
		- portLabel: Get the port value based on the index value
	}
    methods: {
        - arrivalText: Get the arrival text based on whether or not the date of the port has passed
		- departureText: Get the departure text based on whether or not the date of the port has passed
		- dateInPast: Figure out whether or not the arrival/departure date has passed
    }
**/
export default {
	props: ["results"],
	computed: {
		getBolNumber: function () {
			const results = this.results;
			const lastResult = results[results.length - 1];

			return lastResult.bol;
		},
		getBookingNumber: function () {
			const results = this.results;
			const lastResult = results[results.length - 1];

			return lastResult.bookingNo;
		},
		getPorts: function () {
			const results = this.results;
			const ports = [];

			// Loop through all ports to create an array - This does not include the last stop
			results.forEach(function (result, index, array) {
				const previousPort = results[index - 1];

				let port = [];

				const options = { month: "short", day: "numeric" };

				port = {
					port: result.pol,
					expDepartureDate: result.polETS,
					departureDate: result.polATS,
					expArrivalDate: index === 0 ? null : previousPort.podETA,
					arrivalDate: index === 0 ? null : previousPort.podATA,
				};

				ports.push(port);
			});

			const lastResult = results[results.length - 1];

			let lastPort = {
				port: lastResult.pod,
				expDepartureDate: null,
				departureDate: null,
				expArrivalDate: lastResult.podETA,
				arrivalDate: lastResult.podATA,
			};

			ports.push(lastPort);

			return ports;
		},
	},
	filters: {
		formatDate: function (date) {
			if (date) {
				const options = { month: "short", day: "numeric" };
				// Need to format this for Browsers that don't support **-**-**** format.
				const formatDate = date.replace(/-/g, "/");

				return new Date(formatDate).toLocaleDateString("en-GB", options);
			}
		},
		portLabel: function (index, length) {
			// Decide what label based on the index vs the length
			if (index == 0) {
				return "Departure Port";
			} else if (index === length) {
				return "Destination Port";
			} else {
				return "Transhipment Port";
			}
		},
	},
	methods: {
		arrivalText: function (expectedDate, arrivalDate, textClass) {
			if (expectedDate) {
				let formattedExpectDate = this.$options.filters.formatDate(expectedDate);
				let formattedArrivalDate = this.$options.filters.formatDate(arrivalDate);

				if (
					formattedArrivalDate === formattedExpectDate ||
					this.dateInPast(expectedDate, arrivalDate)
				) {
					return (
						'<p class="port-completed port-icon lg:text-sm mb-1 text-success">Arrived ' +
						formattedArrivalDate +
						"</p>"
					);
				} else if (formattedArrivalDate !== formattedExpectDate) {
					return (
						'<p class="port-arrivals port-icon lg:text-sm mb-1 ' +
						textClass +
						'">Expected to arrive ' +
						formattedExpectDate +
						"</p>"
					);
				}
			}
		},
		departureText: function (expectedDate, departedDate, textClass) {
			if (expectedDate) {
				let formattedExpectDate = this.$options.filters.formatDate(expectedDate);
				let formattedDepartedDate = this.$options.filters.formatDate(departedDate);

				if (
					formattedDepartedDate === formattedExpectDate ||
					this.dateInPast(expectedDate, departedDate)
				) {
					return (
						'<p class="port-completed port-icon lg:text-sm mb-1 text-success">Departed ' +
						formattedDepartedDate +
						"</p>"
					);
				} else if (formattedDepartedDate !== formattedExpectDate) {
					return (
						'<p class="port-departure port-icon lg:text-sm mb-1 ' +
						textClass +
						'">Expected to depart ' +
						formattedExpectDate +
						"</p>"
					);
				}
			}
		},
		dateInPast: function (firstDate, secondDate) {
			return Date.parse(new Date(firstDate)) < Date.parse(new Date(secondDate));
		},
	},
};
</script>
